import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import "./Toast.css";

export type ToastTone = "info" | "success" | "warn" | "danger";

export interface ToastInput {
  tone?: ToastTone;
  title: string;
  durationMs?: number;
  action?: { label: string; onClick: () => void };
}

interface ToastWithId extends ToastInput {
  id: string;
}

interface ToastContextValue {
  push: (toast: ToastInput) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastWithId[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
    const tm = timers.current.get(id);
    if (tm) {
      clearTimeout(tm);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (input: ToastInput) => {
      const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setToasts((cur) => [...cur, { ...input, id }]);
      const dur = input.durationMs ?? 5000;
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), dur),
      );
    },
    [dismiss],
  );

  useEffect(() => {
    return () => {
      for (const tm of timers.current.values()) clearTimeout(tm);
      timers.current.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={{ push, dismiss }}>
      {children}
      {createPortal(
        <div className="pr-toast-stack" aria-live="polite">
          {toasts.map((t) => (
            <div key={t.id} className={`pr-toast pr-toast--${t.tone ?? "info"} pr-fadein`} role="status">
              <span className="pr-toast__title">{t.title}</span>
              {t.action && (
                <button
                  className="pr-toast__action"
                  onClick={() => {
                    t.action!.onClick();
                    dismiss(t.id);
                  }}
                >
                  {t.action.label}
                </button>
              )}
              <button className="pr-toast__close" aria-label="Fechar" onClick={() => dismiss(t.id)}>
                ×
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast precisa estar dentro de <ToastProvider>");
  return ctx;
}
